import { ZOOM_LADDER_PROF } from './helpers/packBlocks';

/**
 * Contrôles de réglage par exercice dans l'aperçu print (A− / A+ / ⇆ / ✎ + badges).
 *
 * Rendu dans les DEUX phases (mesure et display) en frère du wrapper de zoom :
 * absolu + hors flux → hauteur nulle, aucune influence sur la mesure, et le
 * DOM des deux phases reste identique au pixel près. Jamais descendant du
 * wrapper zoomé (les contrôles resteraient minuscules sur un exo à 50 %).
 *
 * Règles affichées :
 *   - le réglage prof est un PLAFOND : badge « 85 % » ou « 85 % → 70 % auto »
 *     quand le moteur a dû réduire davantage ;
 *   - paliers prof 100/85/70 ; 55/50 % réservés au moteur (badge rouge avec
 *     issue actionnable : passer en pleine largeur) ;
 *   - forcer la demi-largeur est une demande refusable (badge « demi refusé ») ;
 *   - axe FORMAT (QCM seulement) : bouton ✎ « réponse libre » ↔ ☰ « rétablir
 *     les choix » ; le badge n'apparaît que si l'exercice DIFFÈRE du réglage
 *     global (badge = exception, jamais confirmation).
 *
 * Desktop-first assumé : contrôles au survol, invisibles au tactile.
 */
const ptEffectif = (factor) => Math.round(14 * 0.75 * factor * 10) / 10; // 14px ≈ 10,5pt

const ExoControls = ({
    uid,
    manualFactor,      // réglage prof résolu (1 si aucun)
    effectiveFactor,   // min(manuel, auto) réellement appliqué
    isHalf,            // le bloc est actuellement rendu en demi-colonne
    forced,            // 'full' | 'half' | null (forçage prof résolu)
    refused,           // true si la demande de demi a été refusée (trop large)
    isAlwaysFull,      // type pédagogiquement « toujours pleine largeur »
    onBump,            // (uid, +1|-1) → palier prof suivant
    onToggleDensity,   // (uid, versHalf:boolean)
    reponse = null,    // 'qcm' | 'libre' résolu pour ce bloc, null si hors axe format
    famille = 'qcm',   // 'qcm' | 'tableau' : libellés du bouton et du badge
    reponseGlobal = 'qcm',   // réglage global (badge seulement si différent)
    reponseConvertible = true, // false → QCM à images / tableau d'association, bouton désactivé
    reponseRaison = null,    // explication du refus de conversion
    onToggleReponse = null,  // (uid, 'qcm'|'libre')
}) => {
    const idxProf = ZOOM_LADDER_PROF.findIndex((f) => Math.abs(f - manualFactor) < 1e-6);
    const idx = idxProf === -1 ? ZOOM_LADDER_PROF.length - 1 : idxProf;
    const canPlus = idx > 0;
    const canMinus = idx < ZOOM_LADDER_PROF.length - 1;

    const autoBelow = effectiveFactor < manualFactor - 1e-6;
    const pct = Math.round(effectiveFactor * 100);
    const warn = effectiveFactor < 0.7 - 1e-6 || refused;

    let badgeText = null;
    let badgeTitle = '';
    if (refused) {
        badgeText = 'demi refusé';
        badgeTitle = 'Trop large pour une demi-colonne, même réduit — repassé en pleine largeur.';
    } else if (autoBelow) {
        badgeText = `${Math.round(manualFactor * 100)} % → ${pct} % auto`;
        badgeTitle = `Réduit automatiquement pour tenir sur la page (texte ≈ ${ptEffectif(effectiveFactor)} pt).`
            + (warn ? ' Texte très petit : essayez la pleine largeur.' : '');
    } else if (manualFactor < 1 - 1e-6) {
        badgeText = `${pct} %`;
        badgeTitle = `Taille du texte ≈ ${ptEffectif(effectiveFactor)} pt.`;
    } else if (forced === 'half' || forced === 'full') {
        badgeText = forced === 'half' ? '½ forcé' : 'pleine larg.';
        badgeTitle = forced === 'half' && isAlwaysFull
            ? 'Exercice normalement en pleine largeur (précision du tracé réduite en demi-colonne).'
            : 'Largeur forcée manuellement.';
    }

    const showReponse = reponse !== null && typeof onToggleReponse === 'function';
    const enLibre = reponse === 'libre';
    const estTableau = famille === 'tableau';
    const estMixte = famille === 'mixte';
    const nomSupport = estMixte ? 'exercice' : (estTableau ? 'tableau' : 'QCM');
    const badgeFormat = showReponse && reponseConvertible && reponse !== reponseGlobal
        ? (enLibre ? 'réponse libre' : (estMixte ? 'à cocher' : nomSupport))
        : null;
    let titreReponse;
    if (!reponseConvertible) {
        titreReponse = reponseRaison || (estMixte
            ? 'Cet exercice ne peut pas passer en réponse libre.'
            : `Ce ${nomSupport} ne peut pas passer en réponse libre.`);
    }
    else if (enLibre) titreReponse = estMixte
        ? 'Rétablir les choix et tableaux à cocher sur cet exercice'
        : estTableau ? 'Rétablir le tableau à cocher sur cet exercice' : 'Rétablir les choix du QCM sur cet exercice';
    else titreReponse = estMixte
        ? 'Réponse libre sur cet exercice : les questions à choix ou à tableau passent en réponse rédigée'
        : estTableau
            ? 'Réponse libre sur cet exercice : l\'élève écrit la réponse de chaque ligne (et justifie un Vrai/Faux)'
            : 'Réponse libre sur cet exercice : choix masqués, lignes de réponse';

    const hasBadge = Boolean(badgeText || badgeFormat);

    return (
        <div className={`ep-exo-controls no-print${hasBadge ? ' ep-has-badge' : ''}`}>
            <button
                type="button"
                className="ep-exo-ctl-btn"
                title="Réduire cet exercice (paliers 85 %, 70 %)"
                disabled={!canMinus}
                onClick={() => onBump(uid, +1)}
            >
                A−
            </button>
            <button
                type="button"
                className="ep-exo-ctl-btn"
                title="Agrandir cet exercice"
                disabled={!canPlus}
                onClick={() => onBump(uid, -1)}
            >
                A+
            </button>
            <button
                type="button"
                className="ep-exo-ctl-btn"
                title={isHalf
                    ? 'Passer en pleine largeur'
                    : (isAlwaysFull
                        ? 'Forcer la demi-colonne (⚠️ précision du tracé réduite)'
                        : 'Passer en demi-colonne')}
                onClick={() => onToggleDensity(uid, !isHalf)}
            >
                ⇆
            </button>
            {showReponse ? (
                <button
                    type="button"
                    className="ep-exo-ctl-btn"
                    title={titreReponse}
                    disabled={!reponseConvertible}
                    onClick={() => onToggleReponse(uid, enLibre ? 'qcm' : 'libre')}
                >
                    {enLibre ? '☰' : '✎'}
                </button>
            ) : null}
            {badgeText ? (
                <span
                    className={`ep-exo-badge${warn ? ' ep-badge-warn' : ''}`}
                    title={badgeTitle}
                >
                    {badgeText}
                </span>
            ) : null}
            {badgeFormat ? (
                <span
                    className="ep-exo-badge ep-badge-format"
                    title={enLibre
                        ? 'Cet exercice est en réponse libre alors que le réglage global est « À cocher ».'
                        : `Cet exercice garde son ${nomSupport} alors que le réglage global est « Libres ».`}
                >
                    {badgeFormat}
                </span>
            ) : null}
        </div>
    );
};

export default ExoControls;
